import { useStudyText } from '@/i18n/studyText';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supervisedStudentApi } from '@/features/workspace/api';
import { useAuth } from '@/hooks/useAuth';
import {
  DocumentSample,
  LiteratureItem,
  MeetingConsultation,
  ResearchResult,
  ResearchTopic,
  ScientificWorkData,
  StudentTask,
  TimelineMilestone,
  WorkSection,
} from '../types';
import '../sv121.css';

interface ScientificWorkOverlayProps {
  data?: ScientificWorkData;
  isOpen?: boolean;
  onClose: () => void;
  allowLevelSwitch?: boolean;
  onSwitchLevel?: (level: string) => void;
  onSelectTopic?: (topicId: string, title?: string) => void;
  onOpenCustomTopicModal?: () => void;
  onOpenTaskModal?: (task: StudentTask) => void;
  onOpenLiteratureModal?: () => void;
  onOpenWritingModal?: (section: WorkSection) => void;
  onRespondMeeting?: (meetingId: string, status: string) => void;
  onOpenAiModal?: (context: string) => void;
  onPreviewFile?: (title: string, url: string) => void;
  onContactSupervisor?: () => void;
}

type ViewKey =
  | 'overview'
  | 'topic'
  | 'plan'
  | 'tasks'
  | 'literature'
  | 'writing'
  | 'resources'
  | 'research'
  | 'timeline'
  | 'meetings'
  | 'defense'
  | 'ai'
  | 'official';

const NAV: { key: ViewKey; label: string; icon: string; hint: string; color: string }[] = [
  { key: 'overview', label: 'Обзор', icon: 'dashboard', hint: 'Мои данные и действия', color: '#41d2ff' },
  { key: 'topic', label: 'Моя тема', icon: 'topic', hint: 'Мои данные и действия', color: '#49ddb7' },
  { key: 'plan', label: 'План работы', icon: 'plan', hint: 'Мои данные и действия', color: '#e8b33b' },
  { key: 'tasks', label: 'Мои задачи', icon: 'task', hint: 'Мои данные и действия', color: '#9e80ff' },
  { key: 'literature', label: 'Совместная литература', icon: 'files', hint: 'Мои данные и действия', color: '#66b7e8' },
  { key: 'writing', label: 'Основная работа', icon: 'plan', hint: 'Мои данные и действия', color: '#49ddb7' },
  { key: 'resources', label: 'Образцы и документы', icon: 'files', hint: 'Мои данные и действия', color: '#e8b33b' },
  { key: 'research', label: 'Публикации / НИР', icon: 'research', hint: 'Мои данные и действия', color: '#ff7663' },
  { key: 'timeline', label: 'Практики / этапы', icon: 'calendar', hint: 'Мои данные и действия', color: '#9e80ff' },
  { key: 'meetings', label: 'Встречи и консультации', icon: 'chat', hint: 'Мои данные и действия', color: '#c7a7ff' },
  { key: 'defense', label: 'Предзащита / защита', icon: 'shield', hint: 'Мои данные и действия', color: '#41d2ff' },
  { key: 'ai', label: 'AI-помощник', icon: 'spark', hint: 'Мои данные и действия', color: '#49ddb7' },
  { key: 'official', label: 'Официальные источники ENU', icon: 'files', hint: 'Мои данные и действия', color: '#e8b33b' },
];

const LEVEL_CARDS = [
  { id: 'BACHELOR', num: '01', title: 'Бакалавриат', duration: '4 курс · 1 учебный год', accent: '#41d2ff' },
  { id: 'MASTER', num: '02', title: 'Магистратура', duration: '2 года · 4 семестра', accent: '#9e80ff' },
  { id: 'DOCTORAL', num: '03', title: 'Докторантура', duration: '3 года · 6 семестров', accent: '#49ddb7' },
];

function SvIcon({ name }: { name: string }) {
  const body: Record<string, React.ReactNode> = {
    dashboard: <path d="M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z" />,
    topic: <path d="M4 5h16v14H4zM8 9h8M8 13h6" />,
    plan: <path d="M5 4h14v16H5zM8 8h8M8 12h8M8 16h5" />,
    task: <path d="M5 5h14v14H5zM8 10l2 2 5-5M8 16h8" />,
    files: <path d="M3 7h7l2 2h9v10H3z" />,
    research: <path d="M5 20h14M7 20V8h10v12M9 8V4h6v4M10 12h4M10 16h4" />,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></>,
    chat: <path d="M4 5h16v11H9l-5 4zM8 9h8M8 12h5" />,
    shield: <path d="M12 3l8 3v6c0 5-3 8-8 10-5-2-8-5-8-10V6zM8 12l3 3 5-6" />,
    spark: <path d="M12 2l2.1 5.9L20 10l-5.9 2.1L12 18l-2.1-5.9L4 10l5.9-2.1zM19 17l.8 2.2L22 20l-2.2.8L19 23l-.8-2.2L16 20l2.2-.8z" />,
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {body[name] || body.plan}
    </svg>
  );
}

function badgeOf(status: string): { tone: string; label: string } {
  const map: Record<string, [string, string]> = {
    DONE: ['ok', 'DONE'],
    ACTIVE: ['warn', 'ACTIVE'],
    PLANNED: ['info', 'PLANNED'],
    APPROVED: ['ok', 'Можно использовать'],
    CAN_USE: ['ok', 'Можно использовать'],
    PENDING: ['warn', 'Ожидает решения'],
    PENDING_REVIEW: ['warn', 'Ожидает решения'],
    OFFERED: ['info', 'Предложено'],
    UNDER_REVIEW: ['warn', 'На рассмотрении'],
    DRAFT: ['info', 'Черновик'],
    READY: ['info', 'Готово к работе'],
    ASSIGNED: ['info', 'Назначено'],
    IN_PROGRESS: ['warn', 'В работе'],
    ON_REVIEW: ['warn', 'Отправлено'],
    SUBMITTED: ['warn', 'Отправлено'],
    RETURNED: ['bad', 'На доработке'],
    REJECTED: ['bad', 'Не использовать'],
    SCHEDULED: ['info', 'SCHEDULED'],
  };
  const found = map[status] || ['info', status || '—'];
  return { tone: found[0], label: found[1] };
}

function Badge({ status, raw = false }: { status: string; raw?: boolean }) {
  const st = useStudyText();
  const badge = badgeOf(status);
  return <span className={`sv121-badge ${badge.tone}`}>{st(raw ? status : badge.label)}</span>;
}

function blankWork(): ScientificWorkData {
  return {
    level: 'BACHELOR',
    profile: {
      full_name: '', academia_id: '', program_code: '', program_title: '',
      supervisor_name: 'Не назначен', progress_pct: 0, rating_score: 0,
      rating_rank: '', tasks_count: 0, tasks_approved_count: 0,
      publications_count: 0, publications_required: 0,
    },
    current_topic: { id: '', title: '', status: 'DRAFT', proposed_by: 'STUDENT', is_active: false },
    suggested_topics: [],
    plan: { version_label: '', status: 'DRAFT', sections: [] },
    tasks: [], literature: [], writing_sections: [], document_samples: [],
    research_results: [], milestones: [], consultations: [], defense_checkpoints: [],
    translations: { kz: '', ru: '', en: '', status: 'DRAFT' },
    topic_questions: [], messages: [], final_defense: [], own_topic: null,
    assembly: { file: '', status: 'DRAFT', pre_plagiarism: '', comment: '', passed: false, deadline: '', sent: false },
    plan_conclusion: '', plan_bibliography: '',
  };
}

function isWorkPayload(value: unknown): value is ScientificWorkData {
  if (!value || typeof value !== 'object') return false;
  const row = value as ScientificWorkData;
  return Boolean(row.profile && row.current_topic && Array.isArray(row.tasks));
}

const ScientificWorkContent: React.FC<ScientificWorkOverlayProps> = ({
  data,
  isOpen,
  onClose,
  allowLevelSwitch = false,
  onSwitchLevel = () => {},
  onSelectTopic = () => {},
  onOpenCustomTopicModal = () => {},
  onOpenTaskModal = () => {},
  onOpenLiteratureModal = () => {},
  onOpenWritingModal = () => {},
  onRespondMeeting = () => {},
  onOpenAiModal = () => {},
  onPreviewFile = () => {},
  onContactSupervisor = () => {},
}) => {
  const st = useStudyText();
  const { user } = useAuth();
  const isOwner = Boolean(user?.roles?.includes('SYSTEM_OWNER'));
  const documentInput = useRef<HTMLInputElement>(null);
  const [replaceDocument, setReplaceDocument] = useState<DocumentSample | null>(null);
  const [work, setWork] = useState<ScientificWorkData>(
    data || blankWork(),
  );
  const [view, setView] = useState<ViewKey>('overview');
  const [level, setLevel] = useState(data?.level || 'BACHELOR');
  const [toast, setToast] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [aiText, setAiText] = useState('Выберите действие.');
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<
    | null
    | { kind: 'own' }
    | { kind: 'answer'; id: string; question: string }
    | { kind: 'task'; task: StudentTask }
    | { kind: 'literature' }
    | { kind: 'message' }
    | { kind: 'section'; title: string }
  >(null);
  const [draft, setDraft] = useState('');
  const [ownForm, setOwnForm] = useState({ title: '', reason: '', result: '' });
  const [litForm, setLitForm] = useState({ title: '', section: 'Обзор литературы', note: '' });
  const [relevance, setRelevance] = useState(work.plan.relevance || '');
  const [goal, setGoal] = useState(work.plan.goal || '');
  const [taskLines, setTaskLines] = useState(work.plan.tasks_summary || '');
  const [conclusion, setConclusion] = useState(work.plan_conclusion || '');
  const [bibliography, setBibliography] = useState(work.plan_bibliography || '');
  const [translations, setTranslations] = useState(work.translations || { kz: '', ru: '', en: '', status: 'DRAFT' });
  const [sectionNotes, setSectionNotes] = useState<Record<string, string>>({});

  const notify = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 2400);
  };

  const applyWork = (next: ScientificWorkData) => {
    setWork(next);
    setLevel(next.level || 'BACHELOR');
    setRelevance(next.plan?.relevance || '');
    setGoal(next.plan?.goal || '');
    setTaskLines(next.plan?.tasks_summary || '');
    setConclusion(next.plan_conclusion || '');
    setBibliography(next.plan_bibliography || '');
    setTranslations(next.translations || { kz: '', ru: '', en: '', status: 'DRAFT' });
    setSectionNotes(Object.fromEntries((next.writing_sections || []).map((section) => [section.id, section.student_notes || ''])));
  };

  const reload = async (nextLevel = level) => {
    const raw = await supervisedStudentApi.scientificWork(nextLevel);
    if (isWorkPayload(raw)) applyWork(raw);
  };

  useEffect(() => {
    if (isOpen === false) return;
    let live = true;
    setLoadError(false);
    supervisedStudentApi
      .scientificWork(allowLevelSwitch ? level : undefined)
      .then((raw) => {
        if (live && isWorkPayload(raw)) applyWork(raw);
      })
      .catch(() => { if (live) setLoadError(true); });
    return () => {
      live = false;
    };
  }, [isOpen, user?.id, allowLevelSwitch]);

  const meta = work.level_meta;
  const accent = meta?.accent || '#41d2ff';
  const label = st(meta?.label || (work.level === 'MASTER' ? 'Магистерская диссертация' : work.level === 'DOCTORAL' ? 'Докторская диссертация' : 'Дипломная работа'));
  const profile = work.profile;
  const topics = useMemo(() => {
    const rows = [work.current_topic, ...(work.suggested_topics || [])].filter((topic) => topic?.title);
    const seen = new Set<string>();
    return rows.filter((topic) => {
      if (seen.has(topic.id)) return false;
      seen.add(topic.id);
      return true;
    });
  }, [work]);
  const approvedCount = work.tasks.filter((task) => task.status === 'APPROVED').length;
  const writingApproved = work.writing_sections.filter((section) => section.status === 'APPROVED').length;
  const literatureApproved = work.literature.filter((item) => ['APPROVED', 'CAN_USE'].includes(item.status)).length;

  if (isOpen === false) return null;

  const switchLevel = async (next: string) => {
    onSwitchLevel(next);
    setBusy(true);
    try {
      await reload(next);
    } catch {
      notify(st("Не удалось загрузить выбранный уровень"));
    } finally {
      setBusy(false);
    }
  };

  const saveAll = async () => {
    setBusy(true);
    try {
      await supervisedStudentApi.updatePlan({
        relevance,
        goal,
        tasks_summary: taskLines,
        conclusion,
        bibliography,
        sections: work.plan.sections,
      });
      await supervisedStudentApi.saveTranslations({
        kz: translations.kz,
        ru: translations.ru,
        en: translations.en,
      });
      await reload();
      notify(st('Сохранено'));
    } catch {
      notify(st('Не удалось сохранить. Проверьте соединение и повторите.'));
    } finally {
      setBusy(false);
    }
  };

  const pickTopic = async (topic: ResearchTopic) => {
    onSelectTopic(topic.id, topic.title);
    setBusy(true);
    try {
      await supervisedStudentApi.updateTopic({ topic_id: topic.id, title: topic.title });
      await reload();
      notify(st("Тема выбрана и отправлена руководителю"));
    } catch {
      notify(st("Не удалось выбрать тему"));
    } finally {
      setBusy(false);
    }
  };

  const submitOwnTopic = async () => {
    if (!ownForm.title.trim() || !ownForm.reason.trim()) {
      notify(st("Укажите тему и причину отказа от предложений"));
      return;
    }
    onOpenCustomTopicModal();
    setBusy(true);
    try {
      await supervisedStudentApi.submitCustomTopic({
        title: ownForm.title.trim(),
        justification: `${ownForm.reason.trim()}\n${ownForm.result.trim()}`.trim(),
      });
      setDialog(null);
      await reload();
      notify(st("Собственная тема отправлена руководителю"));
    } catch {
      notify(st("Не удалось отправить тему"));
    } finally {
      setBusy(false);
    }
  };

  const submitAnswer = async () => {
    if (dialog?.kind !== 'answer' || !draft.trim()) return;
    setBusy(true);
    try {
      await supervisedStudentApi.answerTopicQuestion(dialog.id, draft.trim());
      setDialog(null);
      setDraft('');
      await reload();
      notify(st("Ответ сохранён"));
    } catch {
      notify(st("Не удалось сохранить ответ"));
    } finally {
      setBusy(false);
    }
  };

  const submitTask = async () => {
    if (dialog?.kind !== 'task') return;
    onOpenTaskModal(dialog.task);
    setBusy(true);
    try {
      await supervisedStudentApi.submitTask(dialog.task.id, {
        student_comment: draft.trim() || dialog.task.student_comment,
        file_url: dialog.task.file_url || `/files/${dialog.task.task_code || dialog.task.id}.docx`,
      });
      setDialog(null);
      setDraft('');
      await reload();
      notify(st("Работа отправлена руководителю"));
    } catch {
      notify(st("Не удалось отправить задачу"));
    } finally {
      setBusy(false);
    }
  };

  const submitLiterature = async () => {
    if (!litForm.title.trim()) return;
    onOpenLiteratureModal();
    setBusy(true);
    try {
      await supervisedStudentApi.addLiterature({
        title: litForm.title.trim(),
        category_section: litForm.section,
        note: litForm.note,
        file_name: 'student_source.pdf',
      });
      setDialog(null);
      setLitForm({ title: '', section: 'Обзор литературы', note: '' });
      await reload();
      notify(st("Источник отправлен на согласование"));
    } catch {
      notify(st("Не удалось добавить источник"));
    } finally {
      setBusy(false);
    }
  };

  const submitMessage = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await supervisedStudentApi.sendWorkMessage(draft.trim());
      setDialog(null);
      setDraft('');
      await reload();
      notify(st("Сообщение отправлено"));
    } catch {
      notify(st("Не удалось отправить сообщение"));
    } finally {
      setBusy(false);
    }
  };

  const respondMeeting = async (meeting: MeetingConsultation, status: string) => {
    onRespondMeeting(meeting.id, status);
    try {
      await supervisedStudentApi.respondMeeting(meeting.id, status);
      await reload();
      notify(status === 'ACCEPTED' ? 'Участие подтверждено' : 'Приглашение отклонено');
    } catch {
      notify(st("Не удалось обновить встречу"));
    }
  };

  const openFile = (title: string, url?: string | null) => {
    onPreviewFile(title, url || '#');
    notify(`Файл открыт для просмотра: ${title}`);
  };

  const askAi = async (context: string, prompt: string) => {
    onOpenAiModal(context);
    setView('ai');
    try {
      const res = await supervisedStudentApi.askAi(prompt, context);
      setAiText(res?.answer || 'Черновик подготовлен. Решение остаётся за научным руководителем.');
    } catch {
      setAiText('AI готовит только черновик. Окончательное решение утверждает научный руководитель.');
    }
  };

  const submitSection = async (section: WorkSection) => {
    onOpenWritingModal(section);
    try {
      await supervisedStudentApi.submitWritingBlock(section.block_key, {
        student_notes: sectionNotes[section.id] || section.student_notes || 'Документ загружен и отправлен руководителю.',
        file_name: section.file_name || `${section.block_key}.docx`,
      });
      await reload();
      notify(st("Блок отправлен руководителю"));
    } catch {
      notify(st("Не удалось отправить блок"));
    }
  };

  const levelSelector = view === 'overview' ? (
    <section className="sv121-panel" style={{ ['--c' as string]: '#49ddb7', marginBottom: 16 }}>
      <span className="sv121-eyebrow">{st("OWNER PREVIEW · ТРИ РЕЖИМА ОБУЧАЮЩЕГОСЯ")}</span>
      <h2 style={{ margin: '6px 0 4px' }}>{st("Выберите уровень образования")}</h2>
      <p>{st("В реальном кабинете обучающийся увидит только свой назначенный уровень. Здесь три крупные кнопки оставлены для проверки владельцем.")}</p>
      <div className="sv121-levels" style={{ marginTop: 14 }}>
        {LEVEL_CARDS.map((card) => {
          const active = level === card.id;
          return (
            <button
              key={card.id}
              type="button"
              className="sv121-level"
              style={{ ['--c' as string]: card.accent, boxShadow: active ? `0 0 0 2px ${card.accent} inset` : undefined }}
              disabled={busy}
              onClick={() => switchLevel(card.id)}
            >
              <span className="num">{card.num}</span>
              <h3>{st(card.title)}</h3>
              <p>{st(card.duration)}</p>
              <div className="meta">
                <span className={`sv121-badge ${active ? 'ok' : 'info'}`}>{active ? st("Активный preview") : st("Открыть интерфейс")}</span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  ) : (
    <section className="sv121-panel" style={{ ['--c' as string]: '#49ddb7', marginBottom: 14 }}>
      <span className="sv121-eyebrow">{st("OWNER PREVIEW · УРОВЕНЬ ОБУЧАЮЩЕГОСЯ")}</span>
      <div className="sv121-tabs" style={{ margin: '10px 0 0' }}>
        {LEVEL_CARDS.map((card) => (
          <button key={card.id} type="button" className={level === card.id ? 'active' : ''} disabled={busy} onClick={() => switchLevel(card.id)}>
            {st(card.title)} · {st(card.duration)}
          </button>
        ))}
      </div>
    </section>
  );

  const hero = (
    <>
      {loadError ? <p role="alert">{st("Не удалось загрузить досье. Повторите попытку позже.")}</p> : null}
      {allowLevelSwitch ? levelSelector : null}
      <section className="sv121-hero">
        <article className="sv121-panel" style={{ ['--c' as string]: accent }}>
          <span className="sv121-eyebrow">{(meta?.code || work.level)} · Личное исследовательское пространство</span>
          <h2>{label}</h2>
          <p>
            {profile.full_name} · {profile.program_code} · {profile.program_title} · {profile.course_year} {" " + st("курс. Все действия и документы видны только обучающемуся и его научному руководителю.") + " "}</p>
          <div className="sv121-kpis">
            <div className="sv121-kpi"><small>{st("Прогресс")}</small><b>{profile.progress_pct ?? 0}%</b><em>{st("По задачам и этапам")}</em></div>
            <div className="sv121-kpi"><small>{st("Рейтинг")}</small><b>{profile.rating_score ?? 0}</b><em>{"" + st("Место") + " "}{profile.rating_rank || '—'}</em></div>
            <div className="sv121-kpi"><small>{st("Задач")}</small><b>{profile.tasks_count ?? work.tasks.length}</b><em>{profile.tasks_approved_count ?? approvedCount} {" " + st("утверждено") + ""}</em></div>
            <div className="sv121-kpi"><small>{st("Публикаций")}</small><b>{profile.publications_count ?? 0}</b><em>{meta?.publication_note || `Минимум ${profile.publications_required ?? 0}`}</em></div>
          </div>
        </article>
        <article className="sv121-panel" style={{ ['--c' as string]: '#9e80ff' }}>
          <span className="sv121-eyebrow">{st("ВАШ НАУЧНЫЙ РУКОВОДИТЕЛЬ")}</span>
          <h3>{profile.supervisor_name}</h3>
          <p>{profile.supervisor_title}<br />{profile.supervisor_department}</p>
          <div className="sv121-actions">
            <button type="button" className="sv121-btn" onClick={() => { onContactSupervisor(); setView('meetings'); }}>{st("Написать / консультация")}</button>
          </div>
        </article>
      </section>
    </>
  );

  const overview = (
    <>
      {hero}
      <section className="sv121-grid">
        <article className="sv121-card sv121-span8" style={{ ['--c' as string]: accent }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("МОЙ МАРШРУТ")}</span>
              <h3>{st("Следующие действия")}</h3>
              <p>{st("Хронология автоматически формируется по уровню, но руководитель может менять порядок, даты и добавлять этапы.")}</p>
            </div>
            <span className="icon"><SvIcon name="calendar" /></span>
          </header>
          <div className="sv121-timeline" style={{ marginTop: 12 }}>
            {work.milestones.slice(0, 5).map((milestone) => (
              <Milestone key={milestone.id} milestone={milestone} />
            ))}
          </div>
          <div className="sv121-actions">
            <button type="button" className="sv121-btn" onClick={() => setView('timeline')}>{st("Открыть всю хронологию →")}</button>
          </div>
        </article>
        <article className="sv121-card sv121-span4" style={{ ['--c' as string]: '#e8b33b' }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("ТЕМА")}</span>
              <h3>
                {work.current_topic.title
                  || ((work.suggested_topics || []).some((topic) => topic.title)
                    ? st('Тема ещё не утверждена')
                    : st('Темы пока не назначены научным руководителем'))}
              </h3>
              <p>
                {work.current_topic.title
                  ? st('Утверждённая формулировка синхронизирована с руководителем.')
                  : st('Темы пока не назначены научным руководителем')}
              </p>
            </div>
            <span className="icon"><SvIcon name="topic" /></span>
          </header>
          <div className="sv121-actions">
            <button type="button" className="sv121-btn gold" onClick={() => setView('topic')}>{st("Перейти к теме")}</button>
          </div>
        </article>
        <article className="sv121-card sv121-span4" style={{ ['--c' as string]: '#49ddb7' }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("ЗАДАЧИ")}</span>
              <h3>{Math.max(work.tasks.length - approvedCount, 0)} {" " + st("активных") + ""}</h3>
              <p>{st("Каждая задача связана с разделами плана, сроком, ожидаемым результатом и файлами.")}</p>
            </div>
            <span className="icon"><SvIcon name="task" /></span>
          </header>
          <div className="sv121-actions"><button type="button" className="sv121-btn" onClick={() => setView('tasks')}>{st("Открыть задачи")}</button></div>
        </article>
        <article className="sv121-card sv121-span4" style={{ ['--c' as string]: '#66b7e8' }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("МАТЕРИАЛЫ")}</span>
              <h3>{work.document_samples.length + work.literature.length} {" " + st("ресурсов") + ""}</h3>
              <p>{st("Образцы руководителя доступны только для просмотра и скачивания; заменяет и обновляет их руководитель.")}</p>
            </div>
            <span className="icon"><SvIcon name="files" /></span>
          </header>
          <div className="sv121-actions"><button type="button" className="sv121-btn" onClick={() => setView('resources')}>{st("Открыть материалы")}</button></div>
        </article>
        <article className="sv121-card sv121-span4" style={{ ['--c' as string]: '#9e80ff' }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("ВСТРЕЧИ")}</span>
              <h3>{work.consultations.length} {" " + st("консультаций") + ""}</h3>
              <p>{st("Повестка, вопросы, протокол и договорённости по каждой встрече.")}</p>
            </div>
            <span className="icon"><SvIcon name="chat" /></span>
          </header>
          <div className="sv121-actions"><button type="button" className="sv121-btn" onClick={() => setView('meetings')}>{st("Открыть консультации")}</button></div>
        </article>
        <article className="sv121-card sv121-span4" style={{ ['--c' as string]: '#66b7e8' }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("СОВМЕСТНАЯ ЛИТЕРАТУРА")}</span>
              <h3>{literatureApproved} {" " + st("разрешённых источников") + ""}</h3>
              <p>{st("Источники руководителя доступны сразу; предложения обучающегося проходят согласование.")}</p>
            </div>
            <span className="icon"><SvIcon name="files" /></span>
          </header>
          <div className="sv121-actions"><button type="button" className="sv121-btn" onClick={() => setView('literature')}>{st("Открыть литературу")}</button></div>
        </article>
        <article className="sv121-card sv121-span4" style={{ ['--c' as string]: '#e8b33b' }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("ПЛАН → ЗАДАЧИ")}</span>
              <h3>{approvedCount}/{work.tasks.length} {" " + st("задач принято") + ""}</h3>
              <p>{st("Количество задач не равно количеству подразделов: задачи создаются по смыслу и хронологии.")}</p>
            </div>
            <span className="icon"><SvIcon name="task" /></span>
          </header>
          <div className="sv121-actions"><button type="button" className="sv121-btn" onClick={() => setView('tasks')}>{st("Рабочие задачи")}</button></div>
        </article>
        <article className="sv121-card sv121-span4 sv161-demo" style={{ ['--c' as string]: '#49ddb7' }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("ОСНОВНАЯ РАБОТА · DEMO")}</span>
              <h3>{writingApproved}/{work.writing_sections.length} {" " + st("блоков принято") + ""}</h3>
              <p>{st("Демонстрационный обучающийся проведён почти до предзащиты.")}</p>
            </div>
            <span className="icon"><SvIcon name="plan" /></span>
          </header>
          <div className="sv121-actions"><button type="button" className="sv121-btn gold" onClick={() => setView('writing')}>{st("Открыть основной этап")}</button></div>
        </article>
      </section>
    </>
  );

  const topicView = (
    <>
      {hero}
      <section className="sv121-panel" style={{ ['--c' as string]: accent }}>
        <span className="sv121-eyebrow">{st("ЭТАП 1 · ВЫБОР И УТВЕРЖДЕНИЕ ТЕМЫ")}</span>
        <h2 style={{ marginTop: 5 }}>{st("Тема исследования")}</h2>
        <p>{st("Руководитель предлагает минимум 3 темы. Обучающийся выбирает тему либо обосновывает отказ от предложений и формулирует собственное предложение. Затем руководитель может задать уточняющие вопросы.")}</p>
        <div className="sv121-grid" style={{ marginTop: 14 }}>
          {topics.map((topic, index) => (
            <article key={topic.id} className="sv121-card sv121-span6" style={{ ['--c' as string]: ['#41d2ff', '#49ddb7', '#e8b33b', '#9e80ff', '#ff7663'][index % 5] }}>
              <header>
                <div>
                  <span className="sv121-eyebrow">{"" + st("ТЕМА") + " "}{index + 1}</span>
                  <h3>{topic.title}</h3>
                  <p>{topic.description}</p>
                </div>
                <span className="icon"><SvIcon name="topic" /></span>
              </header>
              <div className="sv121-callout" style={{ ['--c' as string]: '#49ddb7', marginTop: 12 }}>
                <b>{st("Что будет сделано:")}</b> {topic.expected_result || topic.supervisor_comment || st("Результат согласуется с руководителем.")}
              </div>
              <div className="sv121-actions">
                <button type="button" className={`sv121-btn ${topic.is_active ? 'mint' : ''}`} disabled={busy || !['OFFERED', 'ASSIGNED', 'APPROVED'].includes(topic.status)} onClick={() => pickTopic(topic)}>
                  {topic.is_active ? st("✓ Выбрано") : ['OFFERED', 'ASSIGNED', 'APPROVED'].includes(topic.status) ? st("Выбрать тему") : st("Ожидает решения руководителя")}
                </button>
              </div>
            </article>
          ))}
        </div>
        <article className="sv121-card" style={{ ['--c' as string]: '#e8b33b', marginTop: 14 }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("НЕ ПОДХОДИТ НИ ОДНА?")}</span>
              <h3>{st("Предложить собственную тему")}</h3>
              <p>{st("Обязательно объясните, почему предложения руководителя не подходят, что вы хотите исследовать и какой результат рассчитываете получить.")}</p>
            </div>
            <span className="icon"><SvIcon name="spark" /></span>
          </header>
          <div className="sv121-actions">
            <button type="button" className="sv121-btn gold" onClick={() => setDialog({ kind: 'own' })}>{st("Предложить свою тему")}</button>
          </div>
        </article>
        {work.own_topic?.title ? (
          <article className="sv121-card" style={{ ['--c' as string]: '#e8b33b', marginTop: 14 }}>
            <span className="sv121-eyebrow">{st("СОБСТВЕННОЕ ПРЕДЛОЖЕНИЕ")}</span>
            <h3>{work.own_topic.title}</h3>
            <p><b>{st("Обоснование:")}</b> {work.own_topic.description}</p>
          </article>
        ) : null}
        <article className="sv121-card" style={{ ['--c' as string]: '#9e80ff', marginTop: 14 }}>
          <span className="sv121-eyebrow">{st("ВОПРОСЫ РУКОВОДИТЕЛЯ")}</span>
          <h3>{st("Проверка исследовательского замысла")}</h3>
          <div className="sv121-list" style={{ marginTop: 10 }}>
            {(work.topic_questions || []).map((question) => (
              <div key={question.id} className="sv121-row">
                <div>
                  <strong>{question.question}</strong>
                  <small>{question.answer || st("Ответ ещё не дан")}</small>
                </div>
                {!question.answer ? (
                  <button type="button" className="sv121-btn" onClick={() => { setDraft(''); setDialog({ kind: 'answer', id: question.id, question: question.question }); }}>{st("Ответить")}</button>
                ) : <Badge status="APPROVED" />}
              </div>
            ))}
          </div>
        </article>
        {(translations.kz || translations.ru || translations.en) ? (
          <article className="sv121-card" style={{ ['--c' as string]: '#49ddb7', marginTop: 14 }}>
            <header>
              <div>
                <span className="sv121-eyebrow">{st("УТВЕРЖДЁННЫЙ ПАКЕТ")}</span>
                <h3>{st("Тема на трёх языках + данные научного руководителя")}</h3>
              </div>
              <span className="icon"><SvIcon name="shield" /></span>
            </header>
            <div className="sv121-formgrid" style={{ marginTop: 12 }}>
              <Field label={st("Қазақша")} full><textarea value={translations.kz} onChange={(event) => setTranslations({ ...translations, kz: event.target.value })} /></Field>
              <Field label={st("Русский")} full><textarea value={translations.ru} onChange={(event) => setTranslations({ ...translations, ru: event.target.value })} /></Field>
              <Field label="English" full><textarea value={translations.en} onChange={(event) => setTranslations({ ...translations, en: event.target.value })} /></Field>
            </div>
            <div className="sv121-callout" style={{ ['--c' as string]: '#9e80ff', marginTop: 12 }}>
              <b>{profile.supervisor_name}</b><br />{profile.supervisor_title}, {profile.supervisor_department}
            </div>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn" onClick={saveAll} disabled={busy}>{st("Сохранить 3 языка")}</button>
            </div>
          </article>
        ) : null}
      </section>
    </>
  );

  const planView = (
    <>
      {hero}
      <section className="sv121-panel" style={{ ['--c' as string]: accent }}>
        <span className="sv121-eyebrow">{st("ЭТАП 2 · ПЛАН ИССЛЕДОВАНИЯ")}</span>
        <h2 style={{ marginTop: 5 }}>{"" + st("Структура работы ·") + " "}{work.plan.version_label}</h2>
        <p>{meta?.topic_hint}</p>
        <div className="sv121-callout" style={{ ['--c' as string]: '#66b7e8', marginTop: 14 }}>
          <b>{st("Статус плана:")}</b> {st(work.plan.status)}{"" + st(". После утверждения руководителем разделы и подразделы используются как контекст задач и основной работы.") + " "}</div>
        {(work.plan.versions || []).length > 0 ? (
          <div className="sv121-list" style={{ marginTop: 12 }}>
            {(work.plan.versions || []).map((item) => (
              <div className="sv121-row" key={`${item.at}-${item.label}`}>
                <div>
                  <strong>{st(item.label)} · {item.note}</strong>
                  <small>{item.at} · {item.actor}</small>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        <div className="sv121-grid" style={{ marginTop: 14 }}>
          <article className="sv121-card sv121-span4" style={{ ['--c' as string]: '#e8b33b' }}>
            <h3>{st("Введение")}</h3>
            <Field label={st("Актуальность")} style={{ marginTop: 10 }}><textarea value={relevance} onChange={(event) => setRelevance(event.target.value)} /></Field>
            <Field label={st("Цель")}><textarea value={goal} onChange={(event) => setGoal(event.target.value)} /></Field>
            <Field label={st("Задачи")}><textarea value={taskLines} onChange={(event) => setTaskLines(event.target.value)} /></Field>
          </article>
          <article className="sv121-card sv121-span8" style={{ ['--c' as string]: '#41d2ff' }}>
            <header>
              <div>
                <h3>{st("Основная часть")}</h3>
                <p>{st("2–3 крупных раздела, внутри 2–4 подраздела.")}</p>
              </div>
              <span className="icon"><SvIcon name="plan" /></span>
            </header>
            <div className="sv121-list" style={{ marginTop: 12 }}>
              {work.plan.sections.map((section) => (
                <div key={section.id} className="sv121-row">
                  <div>
                    <strong>{section.title}</strong>
                    <small>{section.subsections.join(' · ')}</small>
                  </div>
                  <Badge status={section.status || 'APPROVED'} raw />
                </div>
              ))}
            </div>
          </article>
          <article className="sv121-card sv121-span6" style={{ ['--c' as string]: '#49ddb7' }}>
            <h3>{st("Заключение")}</h3>
            <Field label={st("Выводы")} style={{ marginTop: 10 }}><textarea value={conclusion} onChange={(event) => setConclusion(event.target.value)} /></Field>
          </article>
          <article className="sv121-card sv121-span6" style={{ ['--c' as string]: '#9e80ff' }}>
            <h3>{st("Библиография")}</h3>
            <p>{st("Список литературы синхронизируется с материалами по заданиям и публикациям.")}</p>
            <Field label={st("Источники")}><textarea value={bibliography} onChange={(event) => setBibliography(event.target.value)} /></Field>
          </article>
        </div>
        <div className="sv121-actions">
          <button type="button" className="sv121-btn gold" onClick={saveAll} disabled={busy}>{st("Сохранить новую версию")}</button>
          <button type="button" className="sv121-btn" onClick={() => { saveAll(); notify(st("План отправлен руководителю")); }}>{st("Отправить руководителю")}</button>
          <button type="button" className="sv121-btn" onClick={() => setView('tasks')}>{st("Перейти к связанным задачам →")}</button>
        </div>
      </section>
    </>
  );

  const tasksView = (
    <>
      {hero}
      <section className="sv121-panel" style={{ ['--c' as string]: accent }}>
        <span className="sv121-eyebrow">{st("РАБОЧИЙ ЦИКЛ · ПЛАН → ЗАДАЧИ")}</span>
        <h2>{st("Задачи создаются по смыслу и хронологии")}</h2>
        <p>{st("Количество задач не обязано совпадать с количеством разделов. Одна часть плана может содержать несколько последовательных задач.")}</p>
        <div className="sv121-list" style={{ marginTop: 14 }}>
          {work.tasks.map((task) => (
            <article key={task.id} className="sv121-card" style={{ ['--c' as string]: task.status === 'APPROVED' ? '#49ddb7' : task.status === 'RETURNED' ? '#ff7663' : '#e8b33b' }}>
              <header>
                <div>
                  <span className="sv121-eyebrow">{task.task_code} · {st(task.status)}</span>
                  <h3>{task.title}</h3>
                  <p>
                    <b>{st("Описание:")}</b> {task.description}<br />
                    <b>{st("Ожидаемый результат:")}</b> {task.expected_result}<br />
                    <b>{st("Объём:")}</b> {task.volume_label || st("По согласованному объёму")} · <b>{st("Дедлайн:")}</b> {task.deadline}<br />
                    <b>{st("Связано с планом:")}</b> {task.plan_link}
                  </p>
                </div>
                <span className="icon"><SvIcon name="task" /></span>
              </header>
              {task.student_comment ? <div className="sv121-callout" style={{ ['--c' as string]: '#66b7e8', marginTop: 10 }}><b>{st("Сопроводительный текст обучающегося:")}</b> {task.student_comment}</div> : null}
              {task.supervisor_review ? <div className="sv121-callout" style={{ ['--c' as string]: '#49ddb7', marginTop: 10 }}><b>{st("Рецензия:")}</b> {task.supervisor_review}</div> : null}
              <div className="sv121-actions">
                <button type="button" className="sv121-btn" onClick={() => { setDraft(task.student_comment || ''); setDialog({ kind: 'task', task }); }}>
                  {task.student_comment ? st("Обновить и отправить") : st("Загрузить / отправить")}
                </button>
                <button type="button" className="sv121-btn" onClick={() => { setDraft(''); setDialog({ kind: 'message' }); }}>{st("Задать вопрос")}</button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </>
  );

  const literatureView = (
    <>
      {hero}
      <section className="sv121-panel" style={{ ['--c' as string]: '#66b7e8' }}>
        <span className="sv121-eyebrow">{"" + st("СОВМЕСТНАЯ ЛИТЕРАТУРА ·") + " "}{meta?.short || st("Бакалавриат")}</span>
        <h2>{st("Библиотека научного руководителя и обучающегося")}</h2>
        <p>{st("Источники научного руководителя сразу разрешены к использованию. Ваши собственные предложения сначала проверяет руководитель.")}</p>
        <div className="sv121-actions">
          <button type="button" className="sv121-btn gold" onClick={() => setDialog({ kind: 'literature' })}>{st("+ Добавить источник / книгу / файл")}</button>
        </div>
        <div className="sv121-list" style={{ marginTop: 14 }}>
          {work.literature.map((item) => (
            <LiteratureCard key={item.id} item={item} onOpen={() => openFile(item.file_name || item.title, item.file_url)} />
          ))}
        </div>
      </section>
    </>
  );

  const writingView = (
    <>
      {hero}
      <section className="sv121-panel" style={{ ['--c' as string]: '#49ddb7' }}>
        <span className="sv121-eyebrow">{"" + st("ОСНОВНОЙ ЭТАП НАПИСАНИЯ ·") + " "}{label}</span>
        <h2>{st("Структура работы синхронизируется с утверждённым планом")}</h2>
        <p>{st("Титульные листы, задание, содержание, введение, разделы плана, заключение, литература и приложения превращаются в рабочие блоки обучающегося.")}</p>
        <div className="sv161-flow">
          <div style={{ ['--c' as string]: '#41d2ff' }}><small>{st("Структура")}</small><strong>{work.writing_sections.length} {" " + st("блоков") + ""}</strong></div>
          <div style={{ ['--c' as string]: '#9e80ff' }}><small>{st('Принято')}</small><strong>{writingApproved}/{work.writing_sections.length}</strong></div>
          <div style={{ ['--c' as string]: '#e8b33b' }}><small>{st("Общий дедлайн")}</small><strong>{work.assembly?.deadline || '—'}</strong></div>
          <div style={{ ['--c' as string]: '#49ddb7' }}><small>{st("Финальный файл")}</small><strong>{work.assembly?.status || 'DRAFT'}</strong></div>
        </div>
        {!work.assembly?.sent ? <div className="sv121-callout" style={{ ['--c' as string]: '#e8b33b' }}>{st("Структура ещё не отправлена научным руководителем.")}</div> : null}
        <div className="sv161-work-tree">
          {work.writing_sections.map((section) => (
            <article key={section.id} className="sv161-work-node" style={{ ['--c' as string]: section.status === 'APPROVED' ? '#49ddb7' : section.status === 'RETURNED' ? '#ff7663' : '#66b7e8' }}>
              <header>
                <div>
                  <span className="sv121-eyebrow">{section.required === false ? st("НЕОБЯЗАТЕЛЬНЫЙ БЛОК") : st("ОБЯЗАТЕЛЬНЫЙ БЛОК")}</span>
                  <h4>{section.title}</h4>
                  <div className="sv161-statusline">
                    <Badge status={section.status} />
                    <span className="sv121-badge info">{section.version_label}</span>
                    {section.file_name ? <span className="sv121-badge info">📎 {section.file_name}</span> : null}
                  </div>
                </div>
              </header>
              <textarea
                value={sectionNotes[section.id] ?? section.student_notes ?? ''}
                placeholder={st("Текст / пояснение / что загружено по этому блоку")}
                onChange={(event) => setSectionNotes((current) => ({ ...current, [section.id]: event.target.value }))}
              />
              {(section.versions || []).length > 0 ? (
                <div className="sv121-list" style={{ marginTop: 8 }}>
                  {(section.versions || []).map((item) => (
                    <div className="sv121-row" key={`${section.id}-${item.at}-${item.label}`}>
                      <div>
                        <strong>{st(item.label)} · {item.note}</strong>
                        <small>{item.at} · {item.actor}</small>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="sv121-actions">
                <button type="button" className="sv121-btn" onClick={() => submitSection(section)}>{st("Загрузить документ и отправить")}</button>
                {section.file_name ? <button type="button" className="sv121-btn" onClick={() => openFile(section.file_name || section.title, section.file_url)}>{st('Открыть')}</button> : null}
              </div>
            </article>
          ))}
        </div>
        <article className="sv121-card" style={{ ['--c' as string]: '#e8b33b', marginTop: 14 }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("ИТОГОВАЯ СБОРКА И ПРЕДВАРИТЕЛЬНАЯ ПРОВЕРКА")}</span>
              <h3>{st("Собрать общий материал")}</h3>
              <p>{st("После принятия рабочих блоков руководитель собирает единую версию и прикрепляет результат предварительного антиплагиата.")}</p>
            </div>
            <span className="icon"><SvIcon name="shield" /></span>
          </header>
          <div className="sv121-callout" style={{ ['--c' as string]: work.assembly?.passed ? '#49ddb7' : '#e8b33b' }}>
            <b>{work.assembly?.passed ? st("Предварительная проверка руководителя пройдена") : st("Итоговая проверка ещё не завершена")}</b>
            {work.assembly?.file ? <><br />{"" + st("Файл:") + " "}{work.assembly.file}</> : null}
            {work.assembly?.pre_plagiarism ? <><br />{"" + st("Антиплагиат:") + " "}{work.assembly.pre_plagiarism}</> : null}
            {work.assembly?.comment ? <><br />{work.assembly.comment}</> : null}
          </div>
        </article>
      </section>
    </>
  );

  const uploadDocument = async (file?: File) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      notify(st('Размер документа превышает 100 МБ'));
      if (documentInput.current) documentInput.current.value = '';
      return;
    }
    setBusy(true);
    try {
      const samples = await supervisedStudentApi.uploadLevelDocument(work.level, file, replaceDocument?.id);
      setWork((current) => ({ ...current, document_samples: samples }));
      notify(st('Сохранено'));
    } catch (error) {
      notify(error instanceof Error ? error.message : st('Не удалось сохранить. Проверьте соединение и повторите.'));
    } finally {
      setBusy(false);
      setReplaceDocument(null);
      if (documentInput.current) documentInput.current.value = '';
    }
  };

  const deleteDocument = async (sample: DocumentSample) => {
    if (!window.confirm(`${st('Удалить')} «${sample.title}»?`)) return;
    setBusy(true);
    try {
      const samples = await supervisedStudentApi.deleteLevelDocument(work.level, sample.id);
      setWork((current) => ({ ...current, document_samples: samples }));
      notify(st('Сохранено'));
    } catch (error) {
      notify(error instanceof Error ? error.message : st('Не удалось сохранить. Проверьте соединение и повторите.'));
    } finally {
      setBusy(false);
    }
  };

  const openDocument = async (sample: DocumentSample, disposition: 'inline' | 'attachment') => {
    // Open during the click so popup blockers do not reject the asynchronous result.
    const preview = disposition === 'inline' ? window.open('about:blank', '_blank') : null;
    if (disposition === 'inline' && !preview) {
      notify(st('Разрешите открытие новой вкладки для просмотра документа'));
      return;
    }
    if (preview) preview.opener = null;
    try {
      const path = `/supervised-student/level-documents/${work.level}/${encodeURIComponent(sample.id)}/file`;
      const result = await supervisedStudentApi.readLevelDocument(path, disposition);
      const url = URL.createObjectURL(result.blob);
      if (preview) preview.location.href = url;
      else {
        const link = document.createElement('a');
        link.href = url;
        link.download = result.filename || sample.file_name;
        link.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      preview?.close();
      notify(error instanceof Error ? error.message : st('Не удалось загрузить файл'));
    }
  };

  const resourcesView = (
    <>
      {hero}
      <section className="sv121-panel" style={{ ['--c' as string]: accent }}>
        <span className="sv121-eyebrow">{st("ОБЩИЕ МАТЕРИАЛЫ УРОВНЯ")}</span>
        <h2>{"" + st("Образцы, положения и документы ·") + " "}{meta?.short || label}</h2>
        <p>{isOwner ? st('Вы можете загружать, заменять и удалять общие материалы выбранного уровня.') : st("Это единый набор материалов для вашего уровня. Вы можете просматривать и скачивать их; редактирует и заменяет только научный руководитель.")}</p>
        {isOwner && <div className="sv121-actions">
          <input ref={documentInput} type="file" aria-label={st('Загрузить документ')} hidden disabled={busy} onChange={(event) => void uploadDocument(event.target.files?.[0])} />
          <button type="button" className="sv121-btn gold" disabled={busy} onClick={() => { setReplaceDocument(null); documentInput.current?.click(); }}>{st('Загрузить документ')}</button>
          <span>{st('До 100 МБ на документ')}</span>
        </div>}
        <div className="sv121-list" style={{ marginTop: 14 }}>
          {work.document_samples.map((sample) => (
            <div key={sample.id} className="sv121-file">
              <span className="fi"><SvIcon name="files" /></span>
              <div>
                <strong>{sample.title}</strong>
                <small>{sample.file_name} · {sample.file_type} · {sample.version_label}</small>
              </div>
              <div className="sv121-file-actions">
                <button type="button" onClick={() => void openDocument(sample, 'inline')}>{st('Открыть')}</button>
                <button type="button" onClick={() => void openDocument(sample, 'attachment')}>{st('Скачать')}</button>
                {isOwner && <>
                  <button type="button" disabled={busy} onClick={() => { setReplaceDocument(sample); documentInput.current?.click(); }}>{st('Заменить')}</button>
                  <button type="button" disabled={busy} onClick={() => void deleteDocument(sample)}>{st('Удалить')}</button>
                </>}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );

  const nirItems = work.research_results.filter((item) => !isPracticeKind(item.kind) && item.kind !== 'PUBLICATION');
  const practiceItems = work.research_results.filter((item) => isPracticeKind(item.kind));
  const publications = work.research_results.filter((item) => item.kind === 'PUBLICATION');
  const sendResearch = async (item: ResearchResult, patch: { file_url?: string; evidence_url?: string }) => {
    await supervisedStudentApi.submitResearch({
      id: item.id.startsWith('preview-') ? undefined : item.id,
      kind: item.kind,
      title: item.title,
      file_url: patch.file_url,
      evidence_url: patch.evidence_url,
    });
    await reload();
  };

  const researchView = (
    <>
      {hero}
      <section className="sv121-panel" style={{ ['--c' as string]: accent }}>
        <span className="sv121-eyebrow">{st("ИССЛЕДОВАТЕЛЬСКИЕ РЕЗУЛЬТАТЫ")}</span>
        <h2>{meta?.nir_label || st("НИР")}{st(", статьи, конференции и evidence")}</h2>
        <p>
          {meta?.short || label}: {meta?.publication_note}. {work.level === 'BACHELOR'
            ? st("Для бакалавриата предусмотрена как минимум одна апробация / конференция и основной результат дипломной работы.")
            : `Отчёт ${meta?.nir_label || st("НИР")} ведётся по каждому семестру отдельно: основной отчёт и отдельные подтверждающие материалы.`}
        </p>
        <div className="sv121-grid" style={{ marginTop: 14 }}>
          <article className="sv121-card sv121-span6" style={{ ['--c' as string]: '#49ddb7' }}>
            <header>
              <div>
                <h3>{meta?.nir_label || st("Исследовательский результат")}</h3>
                <p>{st("Основной отчёт и evidence хранятся раздельно: статьи, сертификаты, акты, данные и материалы практики.")}</p>
              </div>
              <span className="icon"><SvIcon name="research" /></span>
            </header>
            <div className="sv121-list" style={{ marginTop: 10 }}>
              {nirItems.map((item) => (
                <div key={item.id} className="sv121-row">
                  <div>
                    <strong>{item.title}</strong>
                    <small>
                      {item.file_url ? `Отчёт: ${item.file_url}` : st("Отчёт не загружен")}
                      {item.evidence_url ? ` · evidence: ${item.evidence_url}` : ' · evidence: 0'}
                    </small>
                  </div>
                  <div className="sv121-actions" style={{ margin: 0 }}>
                    <button type="button" className="sv121-btn" onClick={async () => { await sendResearch(item, { file_url: item.file_url || `/files/${item.id}_report.docx` }); notify(st("Отчёт отправлен")); }}>{st("Загрузить отчёт")}</button>
                    <button type="button" className="sv121-btn" onClick={async () => { await sendResearch(item, { evidence_url: item.evidence_url || `/files/${item.id}_evidence.pdf` }); notify(st("Подтверждение добавлено")); }}>{st("+ Подтверждение")}</button>
                  </div>
                </div>
              ))}
            </div>
          </article>
          <article className="sv121-card sv121-span6" style={{ ['--c' as string]: '#e8b33b' }}>
            <header>
              <div>
                <h3>{st("Публикации / конференции")}</h3>
                <p>{st("Результаты по теме: журнал или конференция, статус, venue и подтверждающий файл.")}</p>
              </div>
              <span className="icon"><SvIcon name="research" /></span>
            </header>
            <div className="sv121-list" style={{ marginTop: 10 }}>
              {publications.length ? publications.map((item) => (
                <div key={item.id} className="sv121-row">
                  <div>
                    <strong>{item.title}</strong>
                    <small>{st(item.status)}{item.file_url ? ` · файл: ${item.file_url}` : ''}</small>
                  </div>
                  <button type="button" className="sv121-btn" onClick={async () => { await sendResearch(item, { file_url: item.file_url || '/files/publication.pdf' }); notify(st("Файл публикации обновлён")); }}>{item.file_url ? st("Заменить / добавить файл") : st("+ Файл")}</button>
                </div>
              )) : <div className="sv121-empty">{st("Публикации ещё не добавлены")}</div>}
            </div>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn gold" onClick={async () => {
                await supervisedStudentApi.submitResearch({ kind: 'PUBLICATION', title: 'Конференция / статья по теме', file_url: '/files/publication.pdf' });
                await reload();
                notify(st("Публикация добавлена"));
              }}>{st("+ Добавить публикацию / конференцию")}</button>
            </div>
          </article>
          <article className="sv121-card sv121-span12" style={{ ['--c' as string]: '#66b7e8' }}>
            <header>
              <div>
                <h3>{st("Литература, библиография и материалы по разделам")}</h3>
                <p>{st("Источник или файл связывается с конкретным разделом утверждённого плана и остаётся видимым обеим сторонам.")}</p>
              </div>
              <span className="icon"><SvIcon name="files" /></span>
            </header>
            <div className="sv121-list" style={{ marginTop: 10 }}>
              {work.literature.length ? work.literature.map((item) => (
                <div key={item.id} className="sv121-row">
                  <div>
                    <strong>{item.title}</strong>
                    <small>{"" + st("Раздел") + " "}{item.category_section || '—'} · {item.note || item.author || st("Без комментария")}{item.file_name ? ` · ${item.file_name}` : ''}</small>
                  </div>
                  <span className="sv121-badge info">{item.category_section || st("Материал")}</span>
                </div>
              )) : <div className="sv121-empty">{st("Материалы по разделам ещё не добавлены")}</div>}
            </div>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn" onClick={() => setDialog({ kind: 'literature' })}>{st("+ Добавить источник / файл")}</button>
              <button type="button" className="sv121-btn" onClick={() => setView('literature')}>{st("Открыть совместную литературу")}</button>
            </div>
          </article>
        </div>
        <article className="sv121-card" style={{ ['--c' as string]: '#e8b33b', marginTop: 14 }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("НИР / ПРАКТИКИ / СТАЖИРОВКА · ЕДИНЫЙ ЦИКЛ")}</span>
              <h3>{meta?.short || label} {" " + st("· отчёт → проверка → доработка → утверждение") + ""}</h3>
              <p>{st("Для НИР, практики и научной стажировки применяется та же логика обмена файлами и рецензирования.")}</p>
            </div>
            <span className="icon"><SvIcon name="calendar" /></span>
          </header>
          <div className="sv121-list" style={{ marginTop: 10 }}>
            {[...nirItems.map((item) => ({ item, cycle: 'исследовательская работа' })), ...practiceItems.map((item) => ({ item, cycle: 'практика / стажировка' }))].map(({ item, cycle }) => (
              <div key={item.id} className="sv121-row">
                <div>
                  <strong>{item.title}</strong>
                  <small>{st(item.status)} · {cycle}</small>
                </div>
                <div className="sv121-actions" style={{ margin: 0 }}>
                  <button type="button" className="sv121-btn" onClick={async () => { await sendResearch(item, { file_url: item.file_url || `/files/${item.id}_report.pdf` }); notify(st("Материал отправлен")); }}>{st("Загрузить и отправить")}</button>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>
    </>
  );

  const timelineView = (
    <>
      {hero}
      <section className="sv121-panel" style={{ ['--c' as string]: accent }}>
        <span className="sv121-eyebrow">{st("РЕДАКТИРУЕМАЯ ХРОНОЛОГИЯ")}</span>
        <h2>{meta?.short || label} · {meta?.duration || profile.study_year_label}</h2>
        <p>{st("Вы видите утверждённый руководителем порядок, даты и статусы. Изменения синхронизируются автоматически.")}</p>
        <div className="sv121-timeline" style={{ marginTop: 14 }}>
          {work.milestones.map((milestone) => <Milestone key={milestone.id} milestone={milestone} />)}
        </div>
      </section>
    </>
  );

  const meetingsView = (
    <>
      {hero}
      <section className="sv121-grid sv122-consult-grid">
        <article className="sv121-card sv121-span7" style={{ ['--c' as string]: '#66b7e8' }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("ВСТРЕЧИ · КОНСУЛЬТАЦИИ · ПРИГЛАШЕНИЯ")}</span>
              <h3>{st("Мои консультации")}</h3>
              <p>{st("Каждая встреча имеет дату, повестку, вопросы, протокол и следующие действия.")}</p>
            </div>
            <span className="icon"><SvIcon name="chat" /></span>
          </header>
          <div className="sv121-list" style={{ marginTop: 12 }}>
            {work.consultations.map((meeting) => (
              <div key={meeting.id} className="sv121-row">
                <div>
                  <strong>{meeting.title}</strong>
                  <small>{meeting.scheduled_at} · {st(meeting.status)}{meeting.notes ? ` · ${meeting.notes}` : ''}</small>
                </div>
                <div className="sv121-actions" style={{ margin: 0 }}>
                  {['INVITED', 'PLANNED', 'scheduled'].includes(meeting.status) ? (
                    <>
                      <button type="button" className="sv121-btn mint" onClick={() => respondMeeting(meeting, 'ACCEPTED')}>{st("Принять")}</button>
                      <button type="button" className="sv121-btn danger" onClick={() => respondMeeting(meeting, 'DECLINED')}>{st("Отклонить")}</button>
                    </>
                  ) : null}
                  <button type="button" className="sv121-btn" onClick={() => notify(meeting.notes || meeting.location_or_link || st("Повестка откроется у руководителя"))}>{st('Открыть')}</button>
                </div>
              </div>
            ))}
          </div>
        </article>
        <article className="sv121-card sv121-span5" style={{ ['--c' as string]: '#9e80ff' }}>
          <header>
            <div>
              <span className="sv121-eyebrow">{st("ОБЩАЯ ПЕРЕПИСКА")}</span>
              <h3>{st("Руководитель ↔ обучающийся")}</h3>
              <p>{st("Вопросы по заданиям остаются внутри задач, общие вопросы и договорённости — здесь.")}</p>
            </div>
            <span className="icon"><SvIcon name="chat" /></span>
          </header>
          <div className="sv121-list" style={{ marginTop: 12, maxHeight: 420, overflow: 'auto' }}>
            {(work.messages || []).map((message) => (
              <div key={message.id} className="sv121-row">
                <div>
                  <strong>{message.author}</strong>
                  <small>{message.at} · {message.text}</small>
                </div>
              </div>
            ))}
          </div>
          <div className="sv121-actions">
            <button type="button" className="sv121-btn" onClick={() => { setDraft(''); setDialog({ kind: 'message' }); }}>{st("+ Сообщение")}</button>
          </div>
        </article>
      </section>
    </>
  );

  const defenseView = (
    <>
      {hero}
      <section className="sv121-panel" style={{ ['--c' as string]: accent }}>
        <span className="sv121-eyebrow">{st("ФИНАЛЬНЫЙ МАРШРУТ")}</span>
        <h2>{meta?.short || label} {" " + st("· подготовка к защите") + ""}</h2>
        <p>{st("Сроки и порядок редактируются руководителем. Для каждого этапа можно хранить результат, комментарий и подтверждающий документ.")}</p>
        <div className="sv121-timeline" style={{ marginTop: 14 }}>
          {work.defense_checkpoints.map((step, index) => (
            <div key={step.id} className="sv121-step" style={{ ['--c' as string]: index === 0 ? '#e8b33b' : '#66b7e8' }}>
              <span className="n">{String(step.step_number).padStart(2, '0')}</span>
              <div>
                <h4>{step.title}</h4>
                <p>{step.notes || (index === 0 ? st("Текущий контрольный этап") : st("Следующий этап"))}</p>
              </div>
              <button type="button" className="sv121-btn" onClick={() => notify(`${step.title}: ${step.notes || step.status}`)}>{st('Открыть')}</button>
            </div>
          ))}
        </div>
      </section>
      <section className="sv121-panel" style={{ ['--c' as string]: '#e8b33b', marginTop: 14 }}>
        <span className="sv121-eyebrow">{st("СКВОЗНАЯ ФИНАЛЬНАЯ ТРАЕКТОРИЯ")}</span>
        <h2>{st("От полной работы до основной защиты")}</h2>
        <p>{st("Этапы дополняют ранее утверждённый маршрут и не заменяют существующие требования конкретного уровня.")}</p>
        <div className="sv121-timeline" style={{ marginTop: 12 }}>
          {(work.final_defense || []).map((stage, index) => (
            <div key={stage.key} className="sv121-step" style={{ ['--c' as string]: stage.status === 'DONE' ? '#49ddb7' : stage.status === 'ACTIVE' ? '#e8b33b' : '#66b7e8' }}>
              <span className="n">{String(index + 1).padStart(2, '0')}</span>
              <div>
                <h4>{stage.title}</h4>
                <p>{st(stage.status)}{stage.files.length ? ` · ${stage.files.join(', ')}` : ''}</p>
              </div>
              <Badge status={stage.status} raw />
            </div>
          ))}
        </div>
      </section>
    </>
  );

  const aiView = (
    <>
      {hero}
      <section className="sv121-grid">
        <article className="sv121-card sv121-span8 sv121-ai" style={{ ['--c' as string]: '#9e80ff' }}>
          <header>
            <div>
              <span className="sv121-eyebrow">ИИ-помощник · предварительный режим</span>
              <h3>{st("AI-помощник обучающегося")}</h3>
              <p>{st("AI не утверждает научные решения автоматически. Он готовит проект, который должен проверить человек.")}</p>
            </div>
            <span className="icon"><SvIcon name="spark" /></span>
          </header>
          <div className="sv121-actions">
            <button type="button" className="sv121-btn violet" onClick={() => askAi('topics', 'Предложи темы исследования')}>{st("Темы исследования")}</button>
            <button type="button" className="sv121-btn violet" onClick={() => askAi('questions', 'Вопросы по теме')}>{st("Вопросы по теме")}</button>
            <button type="button" className="sv121-btn violet" onClick={() => askAi('plan', 'Проверить структуру плана')}>{st("Проверить структуру")}</button>
            <button type="button" className="sv121-btn violet" onClick={() => askAi('task', 'Разбить план на задачи')}>{st("Разбить на задачи")}</button>
            <button type="button" className="sv121-btn violet" onClick={() => askAi('risks', 'Риски сроков')}>{st("Риски сроков")}</button>
            <button type="button" className="sv121-btn violet" onClick={() => askAi('feedback', 'Черновик комментария')}>{st("Черновик комментария")}</button>
          </div>
          <div className="sv121-ai-output">{aiText}</div>
        </article>
        <article className="sv121-card sv121-span4" style={{ ['--c' as string]: '#49ddb7' }}>
          <span className="sv121-eyebrow">{st("ПРАВИЛО")}</span>
          <h3>Human approval first</h3>
          <p>{st("Любая тема, оценка, комментарий, изменение плана или решение по защите остаётся под контролем научного руководителя.")}</p>
        </article>
      </section>
    </>
  );

  const officialSources = [
    { title: 'Официальный сайт ENU', url: 'https://enu.kz/', scope: 'Все уровни', text: 'Главный официальный домен университета. Использовать для проверки действующих страниц, объявлений и нормативной информации.' },
    { title: 'Smart ENU', url: 'https://smart.enu.kz/', scope: 'Все уровни', text: 'Официальная информационная система ENU для учебных и персональных сервисов.' },
    { title: 'Практика', url: 'https://enu.kz/ru/page/education/career/practice', scope: 'Все уровни', text: 'Официальная страница ENU по практике и связанным организационным материалам.' },
    ...(work.level === 'DOCTORAL' ? [
      { title: 'Докторантура ENU', url: 'https://enu.kz/ru/page/for-students/phd', scope: 'Докторантура', text: 'Официальный раздел для докторантов.' },
      { title: 'Научная стажировка', url: 'https://enu.kz/ru/page/for-students/phd/scientific-internshi', scope: 'Докторантура', text: 'Официальная страница по научной стажировке докторантов.' },
      { title: 'Диссертационный совет', url: 'https://web.enu.kz/ru/page/university/management-and-structure/intra-university-structures/dissertation-council', scope: 'Докторантура', text: 'Официальная информация о диссертационных советах и процедурах защиты.' },
    ] : []),
  ];

  const officialView = (
    <>
      {hero}
      <section className="sv121-panel" style={{ ['--c' as string]: '#49ddb7' }}>
        <span className="sv121-eyebrow">{st("ОФИЦИАЛЬНЫЕ ИСТОЧНИКИ ENU · EVIDENCE FIRST")}</span>
        <h2>{st("Проверенные точки входа университета")}</h2>
        <p>{st("Этот раздел содержит только официальные домены ENU. Для требований, сроков и форм документов всегда применяется актуальная версия официальной страницы на момент действия.")}</p>
        <div className="sv121-grid" style={{ marginTop: 14 }}>
          {officialSources.map((source, index) => (
            <article key={source.url} className="sv121-card sv121-span6" style={{ ['--c' as string]: ['#49ddb7', '#41d2ff', '#e8b33b', '#9e80ff'][index % 4] }}>
              <header>
                <div>
                  <span className="sv121-eyebrow">{source.scope}</span>
                  <h3>{source.title}</h3>
                  <p>{source.text}</p>
                </div>
                <span className="icon"><SvIcon name="files" /></span>
              </header>
              <div className="sv121-actions">
                <a className="sv121-btn mint" href={source.url} target="_blank" rel="noreferrer">{st("Открыть официальный источник ↗")}</a>
              </div>
            </article>
          ))}
        </div>
      </section>
    </>
  );

  const pages: Record<ViewKey, React.ReactNode> = {
    overview, topic: topicView, plan: planView, tasks: tasksView, literature: literatureView,
    writing: writingView, resources: resourcesView, research: researchView, timeline: timelineView,
    meetings: meetingsView, defense: defenseView, ai: aiView, official: officialView,
  };

  return (
    <div className="sv121-root">
      <div className="sv121-shell">
        <header className="sv121-top">
          <button type="button" className="back" onClick={onClose} aria-label={st("Назад")}>{st("← Назад")}</button>
          <div className="sv121-brand">
            <small>Научная работа под руководством</small>
            <h1>{st("Научная работа под руководством")}</h1>
            <p>{st("Тема · план · задачи · совместная литература · основная работа · образцы и документы · НИР · практика · консультации · защита.")}</p>
          </div>
          <div className="sv121-top-actions">
            <button type="button" className="sv121-btn gold" onClick={saveAll} disabled={busy}>{st('Сохранить')}</button>
            <button type="button" className="sv121-btn" onClick={onClose} aria-label={st("Закрыть")}>{st("Закрыть ×")}</button>
          </div>
        </header>
        <div className="sv121-body">
          <aside className="sv121-side">
            <div className="sv121-rolebox">
              <small>{meta?.code || work.level} · Обучающийся</small>
              <strong>{profile.full_name}</strong>
              <p>{profile.program_code} · {profile.program_title}<br />{profile.course_year} {" " + st("курс ·") + " "}{meta?.duration || profile.study_year_label}</p>
            </div>
            <nav className="sv121-nav">
              {NAV.map((item) => (
                <button key={item.key} type="button" className={view === item.key ? 'active' : ''} style={{ ['--c' as string]: item.color }} onClick={() => setView(item.key)}>
                  <span className="ico"><SvIcon name={item.icon} /></span>
                  <span><strong>{st(item.label)}</strong><small>{st(item.hint)}</small></span>
                  <span className="go">›</span>
                </button>
              ))}
            </nav>
          </aside>
          <main className="sv121-main">{pages[view]}</main>
        </div>
      </div>

      {dialog ? (
        <div className="sv121-dialog open" role="dialog">
          <div className="sv121-dialog-box">
            <header>
              <h2>
                {dialog.kind === 'own' && st("Собственная тема")}
                {dialog.kind === 'answer' && st("Ответ руководителю")}
                {dialog.kind === 'task' && st("Отправить работу")}
                {dialog.kind === 'literature' && st("Новый источник")}
                {dialog.kind === 'message' && st("Сообщение руководителю")}
              </h2>
              <button type="button" onClick={() => setDialog(null)} aria-label={st("Закрыть")}>×</button>
            </header>
            {dialog.kind === 'own' ? (
              <div className="sv121-formgrid" style={{ marginTop: 12 }}>
                <Field label={st("Тема")} full><textarea value={ownForm.title} onChange={(event) => setOwnForm({ ...ownForm, title: event.target.value })} /></Field>
                <Field label={st("Почему не подходят предложения")} full><textarea value={ownForm.reason} onChange={(event) => setOwnForm({ ...ownForm, reason: event.target.value })} /></Field>
                <Field label={st("Ожидаемый результат")} full><textarea value={ownForm.result} onChange={(event) => setOwnForm({ ...ownForm, result: event.target.value })} /></Field>
              </div>
            ) : null}
            {dialog.kind === 'answer' ? (
              <Field label={dialog.question} style={{ marginTop: 12 }}><textarea value={draft} onChange={(event) => setDraft(event.target.value)} /></Field>
            ) : null}
            {dialog.kind === 'task' ? (
              <Field label={st("Сопроводительный текст")} style={{ marginTop: 12 }}><textarea value={draft} onChange={(event) => setDraft(event.target.value)} /></Field>
            ) : null}
            {dialog.kind === 'literature' ? (
              <div className="sv121-formgrid" style={{ marginTop: 12 }}>
                <Field label={st("Название")} full><input value={litForm.title} onChange={(event) => setLitForm({ ...litForm, title: event.target.value })} /></Field>
                <Field label={st("Раздел плана")}><input value={litForm.section} onChange={(event) => setLitForm({ ...litForm, section: event.target.value })} /></Field>
                <Field label={st("Комментарий")}><input value={litForm.note} onChange={(event) => setLitForm({ ...litForm, note: event.target.value })} /></Field>
              </div>
            ) : null}
            {dialog.kind === 'message' ? (
              <Field label={st("Текст")} style={{ marginTop: 12 }}><textarea value={draft} onChange={(event) => setDraft(event.target.value)} /></Field>
            ) : null}
            <div className="sv121-actions">
              <button
                type="button"
                className="sv121-btn gold"
                disabled={busy}
                onClick={() => {
                  if (dialog.kind === 'own') submitOwnTopic();
                  if (dialog.kind === 'answer') submitAnswer();
                  if (dialog.kind === 'task') submitTask();
                  if (dialog.kind === 'literature') submitLiterature();
                  if (dialog.kind === 'message') submitMessage();
                }}
              >
                {st('Сохранить')}
              </button>
              <button type="button" className="sv121-btn" onClick={() => setDialog(null)}>{st("Отмена")}</button>
            </div>
          </div>
        </div>
      ) : null}
      <div className={`sv121-toast${toast ? ' show' : ''}`} role="status">{toast}</div>
    </div>
  );
};

function isPracticeKind(kind: string) {
  return kind.includes('PRACTICE') || kind === 'INTERNSHIP';
}

function Field({
  label,
  full,
  style,
  children,
}: {
  label?: string;
  full?: boolean;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <div className={`sv121-field${full ? ' full' : ''}`} style={style}>
      {label ? <label>{label}</label> : null}
      {children}
    </div>
  );
}

function Milestone({ milestone }: { milestone: TimelineMilestone }) {
  const st = useStudyText();
  const color = milestone.status === 'DONE' ? '#49ddb7' : milestone.status === 'ACTIVE' ? '#e8b33b' : '#66b7e8';
  return (
    <div className="sv121-step" style={{ ['--c' as string]: color }}>
      <span className="n">{String(milestone.order_no).padStart(2, '0')}</span>
      <div>
        <h4>{milestone.title}</h4>
        <p>{milestone.date_deadline} · {st(milestone.status)}</p>
      </div>
      <Badge status={milestone.status} raw />
    </div>
  );
}

function LiteratureCard({ item, onOpen }: { item: LiteratureItem; onOpen: () => void }) {
  const st = useStudyText();
  const fromSupervisor = item.added_by === 'SUPERVISOR';
  return (
    <article className="sv121-card" style={{ ['--c' as string]: fromSupervisor ? '#49ddb7' : '#66b7e8' }}>
      <header>
        <div>
          <span className="sv121-eyebrow">{fromSupervisor ? st("ОТ РУКОВОДИТЕЛЯ") : st("ОТ ОБУЧАЮЩЕГОСЯ")}</span>
          <h3>{item.title}</h3>
          <p>{"" + st("Раздел:") + " "}{item.category_section || st("Без привязки")} · {item.note || item.author || st("Без комментария")}</p>
        </div>
        <span className="icon"><SvIcon name="files" /></span>
      </header>
      <div className="sv161-mini-files">
        <span>📎 {item.file_name || st("Без файла")}</span>
      </div>
      <div className="sv121-actions">
        <Badge status={item.status} />
        <button type="button" className="sv121-btn" onClick={onOpen}>{st('Открыть')}</button>
      </div>
    </article>
  );
}


// A different account gets a new component instance, including every form and dialog.
export const ScientificWorkOverlay: React.FC<ScientificWorkOverlayProps> = (props) => {
  const { user } = useAuth();
  return <ScientificWorkContent key={user?.id || 'anonymous'} {...props} />;
};

export default ScientificWorkOverlay;
